import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';

const RentalContainer = styled.div`
    padding: 2rem 10rem;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        flex-direction: column;
        padding: 2rem 0;
    }
`;

const TabContainer = styled.div`
    display: flex;
    justify-content: left;
    margin-bottom: 2rem;
    flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
    padding: 0.5rem 1rem;
    font-size: 1rem;
    background-color: ${props => props.$active ? '#4CAF50' : '#f0f0f0'};
    color: ${props => props.$active ? 'white' : 'black'};
    border: none;
    cursor: pointer;
    margin: 0.5rem;

    &:hover {
        background-color: ${props => props.$active ? '#45a049' : '#e0e0e0'};
    }

    @media (max-width: 768px) {
        flex: 1 0 auto;
        font-size: 0.9rem;
        padding: 0.4rem 0.8rem;
    }
`;

const ContentSection = styled.section`
    margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
`;

const SectionSubTitle = styled.h3`
    margin-bottom: 1rem;
`;

const Text = styled.p`
    font-size: 1rem;
    line-height: 1.6;
    white-space: pre-wrap;
`;

const List = styled.ul`
    list-style-type: disc;
    margin-left: 2rem;
    margin-bottom: 1rem;
`;

const ListItem = styled.li`
    font-size: 1rem;
    line-height: 1.6;
`;

const CalendarFrame = styled.iframe`
    width: 100%;
    height: 600px;
    border: 0;
    margin-top: 1rem;
    margin-bottom: 2rem;
`;

const Image = styled.img`
    width: 100%;
    height: auto;
    border-radius: 8px;
`;

const Rental: React.FC = () => {
    const { tab } = useParams<{ tab?: string }>();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('reservation');

    const tabs = [
        {id: 'reservation', label: '예약 안내'},
        {id: 'room', label: '공간 사진'},
        {id: 'book', label: '공간 예약'},
    ];

    useEffect(() => {
        if (tab) {
            setActiveTab(tab);
        }
    }, [tab]);

    const handleTabChange = (newTab: string) => {
        setActiveTab(newTab);
        navigate(`/rental/${newTab}`);
    };

    return (
        <RentalContainer>
            <h2>Rêve moiré rental</h2>
            <TabContainer>
                {tabs.map(tab => (
                    <Tab
                        key={tab.id}
                        $active={activeTab === tab.id}
                        onClick={() => handleTabChange(tab.id)}
                    >
                        {tab.label}
                    </Tab>
                ))}
            </TabContainer>

            {activeTab === 'reservation' && (
                <>
                    <ContentSection>
                        <SectionTitle>Rêve moiré rental Notice</SectionTitle>
                        <Text>
                            레브무아르(Rêve moiré)는 조용한 공간에 위치한 플로리스트 작업실입니다. 프랑스어로 'Rêve'는 "꿈"을, 'Moiré'는 "물결무늬"를 의미하여,
                            'Rêve moiré'는 "꿈결"이라는 뜻을 갖습니다.

                            이곳은 비슷한 결을 가진 사람들이 모여 서로의 꿈을 나누고, 응원하며 함께 성장하는 공간입니다. 각자의 고유한 결이 모여 풍성한 꿈의 풍경을 만들어내며, 흐릿한
                            꿈이든 선명한 꿈이든 이곳에서 만나 서로 영감을 주고받기를 희망합니다.

                            여러분의 꿈이 이곳에서 시작되어 아름다운 현실로 피어나기를 진심으로 응원합니다.
                        </Text>
                    </ContentSection>
                    <ContentSection>
                        <SectionSubTitle>예약 안내</SectionSubTitle>
                        <Text>
                            아래 양식을 작성하여 카카오톡 채널 "메이플레르"로 보내주시면 연락드리겠습니다.
                        </Text>
                        <br/>
                        <h4>[예약 양식]</h4>
                        <List>
                            <ListItem>성함</ListItem>
                            <ListItem>연락처</ListItem>
                            <ListItem>대관 날짜 및 시간</ListItem>
                            <ListItem>인원</ListItem>
                            <ListItem>대관 목적</ListItem>
                            <ListItem>렌탈 및 주문 여부</ListItem>
                            <ListItem>차량 여부 (1대만 가능)</ListItem>
                        </List>
                        <Text>
                            예약글 작성 후 입금해주시면 예약이 확정됩니다.
                        </Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionSubTitle>공간 대여</SectionSubTitle>
                        <List>
                            <ListItem>시간당 4만원 / 4인 기준 (최대 6인) / *최소 2시간 이용</ListItem>
                            <ListItem>기준 인원 초과 시 1인당 5천원 추가 (1시간 기준)</ListItem>
                            <ListItem>부가세 별도</ListItem>
                            <ListItem>영유아도 인원수에 포함됩니다.</ListItem>
                            <ListItem>건물 규정상 반려동물은 입실이 불가합니다.</ListItem>
                            <ListItem>음식물 섭취 및 배달 음식 금지 (간단한 음료 및 간식, 디저트만 가능)</ListItem>
                            <ListItem>입금 순서대로 예약이 확정됩니다.</ListItem>
                        </List>
                    </ContentSection>

                    <ContentSection>
                        <SectionSubTitle>테이블 장식</SectionSubTitle>
                        <List>
                            <ListItem><b>기본 테이블</b>
                                <Text>기본 2시간: 9만원 (기본 2시간 공간대여 + 기본 테이블 세팅 1만원)</Text>
                                <Text>포함 항목: 모형 케이크, 케이크 스탠드, 작은 화병 3~4개, 2단 트레이, 식기, 조화 부케, 조화 화관, 찻잔, 드라이 리스, 토퍼</Text>
                            </ListItem>
                            <ListItem><b>숲속 친구들 원더랜드</b>
                                <Text>기본 2시간: 10만원 (기본 2시간 공간대여 + 숲속 친구들 테이블 세팅 ２만원)</Text>
                                <Text>포함 항목: 모형 케이크, 우드 케이크 스탠드, 동물 인형들, 패브릭 쿠션, 조화 부케, 조화 화관, 드라이 리스, 우드 토퍼</Text>
                            </ListItem>
                            <ListItem><b>돌, 백일 테이블</b>
                                <Text>기본 2시간: 10만원 (기본 2시간 공간대여+돌 백일 테이블 세팅 2만원)</Text>
                                <Text>포함 항목: 돌/백일 갈랜드, 돌/백일 토퍼, 모형 백설기, 모형 수수팥떡, 모형 꿀떡, 모형 케이크, 케이크 스탠드, 돌잡이 세트, 조화 화관, 조화 부케, 드라이 벽 리스 </Text>
                            </ListItem>
                        </List>
                        <Text>* 풍선, 추가 토퍼, 의상, 파티햇 등은 직접 준비해주시기 바랍니다.</Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionSubTitle>렌탈 상품</SectionSubTitle>
                        <Text>렌탈 상품은촬영 시 '대여'만 가능하며, 주문 상품은 촬영 후 가져가실 수 있습니다.</Text>
                        <List>
                            <ListItem>렌탈 조화 센터피스 1만원</ListItem>
                            <ListItem>렌탈 파스텔 랜덤 생화 센터피스 3만 5천원 (색감 지정 불가, 색상은 최소 일주일 전에 확인 가능)</ListItem>
                            <ListItem>렌탈 색감 지정 생화 센터피스화 5만 5천원 (최소 일주일 전에만 색감 지정 가능)</ListItem>
                        </List>
                    </ContentSection>

                    <ContentSection>
                        <SectionSubTitle>플라워 공간 스타일링</SectionSubTitle>
                        <List>
                            <ListItem>기본 공간 스타일링: 특별한 순간을 위한 플라워 세팅, 웨딩, 화보, 기념일 촬영 등 특별한 순간을 더욱 빛나게 해줄 기본 플로럴 구성. 다양한
                                촬영 컨셉에 맞춰 공간을 아름답게 연출합니다.
                                <Text>-바닥 생화 센터피스 2개, 웨딩 부케 1개, 부토니아 1개 구성 (40~80만원, 센터피스와 부케 사이즈에 따라 비용 상이)</Text>
                            </ListItem>
                            <ListItem>맞춤형 추가 상품: 기본 공간 스타일링에 추가 상품이 필요하거나, 특정한 상품만 필요하신 분들을 위한 개별 선택 상품
                                <Text>-바닥 센터피스 1개 (10~50만원), 촬영 부케 (15만원), 서브 부케 (7만원), 미니 아치 (40~80만원), 웨딩 아치
                                    (80~200만원)</Text>
                            </ListItem>
                            <ListItem>디자인 스타일링: 소량의 꽃으로 세련된 공간 연출을 원하시는 분들을 위한 스타일링
                                <Text>-화병 테이블 스타일링: 화병에 원하는 색감의 꽃을 넣어 장식하는 테이블 스타일링</Text>
                                <Text>-과일 스타일링: 원하는 과일(오렌지, 자몽, 레몬 등)에 꽃을 함께 장식하는 디자인</Text>
                            </ListItem>
                        </List>
                        <Text>공간 스타일링의 경우 최소 7일전에 주문이 가능합니다. 플로리스트가 현장에 동행하지 않고 작업실에서 공간을 세팅하는 비용입니다. 꽃의 종류와 원하는 분위기,
                            사용되는 꽃의 양에 따라 가격이 조정될 수 있습니다.</Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionSubTitle>안내 사항</SectionSubTitle>
                        <List>
                            <ListItem>작업실에는 냉난방기, 싱크대, 마샬 블루투스 스피커, 접시, 컵, 와인잔이 구비되어 있습니다.</ListItem>
                            <ListItem>주차는 1대만 가능하며, 추가 차량은 인근 공영주차장을 이용해 주시기 바랍니다.</ListItem>
                            <ListItem>예약 시간에는 준비 시간과 정리 시간을 포함해 넉넉하게 예약해 주세요.</ListItem>
                            <ListItem>퇴실 시 냉난방기 전원과 조명을 모두 꺼주세요.</ListItem>
                            <ListItem>촬영에 적합한 시간대는 10시~15시 사이로, 이때 자연광이 좋습니다.</ListItem>
                            <ListItem>스튜디오 내외부 보안 및 안전을 위해 CCTV가 녹화되고 있습니다.</ListItem>
                            <ListItem>상업적 목적 또는 스냅 작가와 동행할 경우 미리 말씀해 주세요.</ListItem>
                            <ListItem>대관 관련 문의는 @revemoire_ 인스타그램 계정 또는 카카오톡채널(메이플레르)로 연락 주세요. 카카오톡채널에서 메이플레르 또는
                                mayfleur로 검색하시면 됩니다.</ListItem>
                            <ListItem>상담 시간: 화~금 9:00~16:00, 토 9:00~15:00 (일, 월은 휴무. 휴무일에는 상담 서비스가 제공되지
                                않습니다.)</ListItem>
                            <ListItem>꽃 수업, 주문 상품 제작, 외부 작업 등으로 인해 메시지 확인이 늦어질 수 있으니, 시간적 여유를 두고 문의 바랍니다. (당일 및 하루 전
                                메시지는 확인이 어려울 수 있습니다.)</ListItem>
                        </List>
                    </ContentSection>

                    <ContentSection>
                        <SectionSubTitle>취소 및 변경 정책</SectionSubTitle>
                        <List>
                            <ListItem>대관일 7일 전: 100% 환불 및 일정 변경 가능</ListItem>
                            <ListItem>대관일 6일 전: 취소 및 환불 불가, 대관일 5일 전에 한해 일정 변경 1회 가능</ListItem>
                            <ListItem>주의: 모든 기간은 대관일을 기준으로 계산됩니다. 예를 들어, 15일이 대관일이라면 9일부터는 취소 및 환불이 불가능하며, 10일에만 일정 변경이
                                1회 가능합니다.
                                이 취소 및 변경 정책을 꼭 유의해주시기 바라며, 이해와 협조 부탁드립니다. </ListItem>
                        </List>
                    </ContentSection>
                    <ContentSection>
                        <SectionSubTitle>주의 사항</SectionSubTitle>
                        <List>
                            <ListItem>작업실에는 고가의 가구와 영국, 프랑스, 독일 등의 빈티지 소품 및 식기가 구비되어 있습니다. 빈티지 가구와 소품은 다시 구하기 어려운 경우가
                                많으니, 파손 시 제품 구입 가격으로 배상해주셔야 합니다. 훼손 시에는 꼭 연락 바랍니다. 활동량이 많은 영유아는 부모님의 주의가
                                필요합니다.</ListItem>
                            <ListItem>사진 촬영 시 삼각대를 바닥에 끌지 않도록 주의해 주세요. 삼각대를 사용할 때는 바닥에 천을 깔고 촬영해 주세요. 바닥에 흠집이 날 경우, 수리
                                비용이 청구될 수 있으니 각별히 주의해 주시기 바랍니다.</ListItem>
                            <ListItem>가구 및 소품, 장식 등을 이동할 경우 사전 협의가 필요하며, 사용 후 원상복구해 주세요.</ListItem>
                        </List>
                    </ContentSection>
                </>
            )}

            {activeTab === 'room' && (
                <ContentSection>
                    <SectionTitle>대여하는 공간 사진</SectionTitle>
                    {[...Array(39)].map((_, index) => (
                            <Image 
                                key={index + 1}
                                src={`${process.env.PUBLIC_URL}/rental/${index + 1}.jpeg`} 
                                alt={`공간 사진 ${index + 1}`}
                            />
                        ))}
                </ContentSection>
            )}

            {activeTab === 'book' && (
                <ContentSection>
                    <SectionTitle>공간 예약</SectionTitle>
                    <Text>월, 화, 토요일의 예약 일정을 확인하실 수 있습니다. 토요일은 오후 3시부터 예약이 가능합니다.</Text>
                    <CalendarFrame
                        src="https://calendar.google.com/calendar/embed?src=hcbo80ej0t380t68gehn1bg8p4%40group.calendar.google.com&ctz=Asia%2FSeoul"
                    />
                </ContentSection>
            )}
        </RentalContainer>
    );
};

export default Rental;